<template>
  <b-modal
    id="modal-add-misc-record"
    title="新增雜項銀行進支記錄"
    size="lg"
    centered
    hide-footer
    no-close-on-backdrop
    @hidden="safeResetForm"
  >
    <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
      <b-overlay
        :show="loading"
        spinner-variant="primary"
        spinner-type="grow"
        spinner-small
        rounded="sm"
      >
        <b-form @submit.prevent="handleSubmit(onSubmit)">
          <b-row>
            <!-- 日期 -->
            <b-col cols="12" md="6">
              <validation-provider
                #default="validationContext"
                name="日期"
                rules="required"
                vid="v_date"
              >
                <b-form-group
                  label="日期："
                  label-for="date"
                  label-cols-md="4"
                  class="required"
                >
                  <flat-pickr
                    id="date"
                    v-model="formData.date"
                    class="form-control"
                    :config="{
                      allowInput: true,
                      altInput: true,
                      altFormat: 'Y-m-d',
                      dateFormat: 'Y-m-d',
                    }"
                    placeholder="輸入YYYY-MM-DD"
                  />
                  <b-form-invalid-feedback
                    :state="getValidationState(validationContext)"
                  >
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- 進支詳情 -->
            <b-col cols="12" md="6">
              <validation-provider
                #default="validationContext"
                name="進支詳情"
                rules="required"
                vid="v_transaction_detail"
              >
                <b-form-group
                  label="進支詳情："
                  label-for="transaction_detail"
                  label-cols-md="4"
                  class="required"
                >
                  <b-form-input
                    id="transaction_detail"
                    v-model="formData.transaction_detail"
                    placeholder="輸入進支詳情"
                    :state="getValidationState(validationContext)"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- 進支方式 -->
            <b-col cols="12" md="6">
              <validation-provider
                #default="validationContext"
                name="進支方式"
                rules="required"
                vid="v_payment_method_id"
              >
                <b-form-group
                  label="進支方式："
                  label-for="payment_method_id"
                  label-cols-md="4"
                  class="required"
                >
                  <v-select
                    id="payment_method_id"
                    v-model="formData.payment_method_id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="paymentMethodOptions"
                    :clearable="true"
                    label="name"
                    :reduce="(option) => option.id"
                    placeholder="選擇進支方式"
                    :filter="paymentMethodSearch"
                  />
                  <b-form-invalid-feedback
                    :state="getValidationState(validationContext)"
                  >
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- 支票號碼 -->
            <b-col cols="12" md="6">
              <validation-provider
                #default="validationContext"
                name="支票號碼"
                :rules="formData.payment_method_id == 1 ? 'required' : ''"
                vid="v_check_number"
              >
                <b-form-group
                  label="支票號碼："
                  label-for="check_number"
                  label-cols-md="4"
                  :class="formData.payment_method_id == 1 ? 'required' : ''"
                >
                  <b-form-input
                    id="check_number"
                    v-model="formData.check_number"
                    placeholder="輸入支票號碼"
                    :state="getValidationState(validationContext)"
                    :readonly="formData.payment_method_id != 1"
                    :disabled="formData.payment_method_id != 1"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- 存入金額 -->
            <b-col cols="12" md="6">
              <validation-provider
                #default="validationContext"
                name="存入金額"
                :rules="!formData.withdrawal_amount ? 'required' : ''"
                vid="v_deposit_amount"
              >
                <b-form-group
                  label="存入金額："
                  label-for="deposit_amount"
                  label-cols-md="4"
                  :class="!formData.withdrawal_amount ? 'required' : ''"
                >
                  <b-form-input
                    id="deposit_amount"
                    v-model="formData.deposit_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    :state="getValidationState(validationContext)"
                    @input="onDepositAmountChange"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- 支出金額 -->
            <b-col cols="12" md="6">
              <validation-provider
                #default="validationContext"
                name="支出金額"
                :rules="!formData.deposit_amount ? 'required' : ''"
                vid="v_withdrawal_amount"
              >
                <b-form-group
                  label="支出金額："
                  label-for="withdrawal_amount"
                  label-cols-md="4"
                  :class="!formData.deposit_amount ? 'required' : ''"
                >
                  <b-form-input
                    id="withdrawal_amount"
                    v-model="formData.withdrawal_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    :state="getValidationState(validationContext)"
                    @input="onWithdrawalAmountChange"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- 儲蓄/往來 -->
            <b-col cols="12" md="6">
              <b-form-group
                label="儲蓄/往來："
                label-for="is_savings"
                label-cols-md="4"
              >
                <b-form-checkbox
                  id="is_savings"
                  v-model="formData.is_savings"
                  switch
                  inline
                  class="savings-switch"
                >
                  <span class="switch-label">
                    {{ formData.is_savings ? '儲蓄' : '往來' }}
                  </span>
                </b-form-checkbox>
              </b-form-group>
            </b-col>

            <!-- 備註 -->
            <b-col cols="12" md="6">
              <b-form-group
                label="備註："
                label-for="remark"
                label-cols-md="4"
              >
                <b-form-input
                  id="remark"
                  v-model="formData.remark"
                  placeholder="輸入備註..."
                  class="remark-input"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- 表單按鈕 -->
          <div class="d-flex justify-content-end mt-3">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="$bvModal.hide('modal-add-misc-record')"
            >
              取消
            </b-button>
            <b-button
              variant="primary"
              type="submit"
              :disabled="loading"
            >
              <b-spinner v-if="loading" small class="mr-1" />
              {{ loading ? '儲存中...' : '儲存' }}
            </b-button>
          </div>
        </b-form>
      </b-overlay>
    </validation-observer>
  </b-modal>
</template>

<script>
import {
  BModal,
  BOverlay,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BFormInvalidFeedback,
  BButton,
  BSpinner,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { ref } from "@vue/composition-api";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import formValidation from "@core/comp-functions/forms/form-validation";
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: "ModalAddMiscRecord",
  components: {
    BModal,
    BOverlay,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormInvalidFeedback,
    BButton,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    flatPickr,
  },
  props: {
    paymentMethodOptions: {
      type: Array,
      required: true,
    },
  },
  setup(props, { emit }) {
    const formData = ref({
      date: null,
      transaction_detail: '',
      payment_method_id: null,
      check_number: '',
      deposit_amount: null,
      withdrawal_amount: null,
      is_savings: false,
      remark: '',
    });

    const loading = ref(false);

    const resetFormData = () => {
      formData.value = {
        date: null,
        transaction_detail: '',
        payment_method_id: null,
        check_number: '',
        deposit_amount: null,
        withdrawal_amount: null,
        is_savings: false,
        remark: '',
      };
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetFormData);

    // 安全的重置表單方法
    const safeResetForm = () => {
      resetFormData();
      // 確保 refFormObserver 存在且已掛載後再重置
      if (refFormObserver.value && typeof refFormObserver.value.reset === 'function') {
        refFormObserver.value.reset();
      }
    };

    // 存入金額變化時清空支出金額
    const onDepositAmountChange = () => {
      if (formData.value.deposit_amount && parseFloat(formData.value.deposit_amount) > 0) {
        formData.value.withdrawal_amount = null;
      }
    };

    // 支出金額變化時清空存入金額
    const onWithdrawalAmountChange = () => {
      if (formData.value.withdrawal_amount && parseFloat(formData.value.withdrawal_amount) > 0) {
        formData.value.deposit_amount = null;
      }
    };
    const paymentMethodSearch = (options, search) => {
      return options.filter((item) => {
        return item.name.toLowerCase().includes(search.toLowerCase());
      });
    }

    const onSubmit = () => {
      loading.value = true;

      // 準備提交數據
      const submitData = {
        date: formData.value.date,
        transaction_detail: formData.value.transaction_detail,
        payment_method_id: formData.value.payment_method_id,
        check_number: formData.value.check_number || '',
        deposit_amount: formData.value.deposit_amount ? parseFloat(formData.value.deposit_amount) : 0,
        withdrawal_amount: formData.value.withdrawal_amount ? parseFloat(formData.value.withdrawal_amount) : 0,
        is_savings: formData.value.is_savings,
        remark: formData.value.remark || '',
      };

      store
        .dispatch("bankRecords/addMiscRecord", submitData)
        .then((response) => {
          loading.value = false;

          // 顯示成功提示
          emit('success', {
            component: ToastificationContent,
            props: {
              title: "新增成功",
              text: response.data.message || "雜項銀行進支記錄已成功新增",
              icon: "CheckCircleIcon",
              variant: "success",
            },
          });

          // 重置表單
          safeResetForm();

          // 關閉 Modal
          emit('hide-modal');

          // 重新載入列表
          emit('refetch-data');
        })
        .catch((error) => {
          loading.value = false;
          console.error('新增雜項記錄失敗:', error);

          // 顯示錯誤提示
          emit('error', {
            component: ToastificationContent,
            props: {
              title: "新增失敗",
              text: error.response?.data?.message || "新增雜項銀行進支記錄失敗，請稍後再試",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    return {
      formData,
      loading,
      refFormObserver,
      getValidationState,
      resetForm,
      safeResetForm,
      onDepositAmountChange,
      onWithdrawalAmountChange,
      onSubmit,
      paymentMethodSearch
    };
  },
};
</script>


<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style lang="scss" scoped>
// 必填欄位樣式
.required label::after {
  content: " *";
  color: #dc3545;
}

// 儲蓄/往來開關樣式
.savings-switch {
  .switch-label {
    margin-left: 0.5rem;
    color: #495057;
    font-weight: 500;
  }
}

// 備註輸入框樣式
.remark-input {
  border: 1px solid #e3e6f0;
  border-radius: 0.25rem;

  &:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
}

// 表單組樣式
.form-group {
  margin-bottom: 1rem;
}

// 金額輸入框樣式
input[type="number"] {
  text-align: right;
}
</style>
